import { useState } from 'react';
import { Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { lookupCustomerByDocument } from '../../../../../services/PublicBookingService';
import {
    EMAIL_PATTERN,
    formatCpf,
    formatIdentifier,
    isCompleteIdentifier,
    isEmailIdentifier,
    looksLikeDocument,
    toIdentifierParam
} from '../../../../../utils/PublicBookingIdentifier';
import { hasActiveOnlineBookingBlock, lookupStatusFields } from '../../../../../utils/PublicBookingCustomer';

/**
 * Cadastro que já satisfaz o que o formulário exigiria: nome, telefone, e-mail e CPF válidos.
 * Só nesse caso dá para pular a conferência de dados sem deixar passar cadastro incompleto.
 *
 * O CPF entra aqui porque ele é obrigatório para agendar: quem se identificou pelo e-mail e
 * não tem documento cadastrado precisa cair no formulário para informá-lo.
 */
const isReadyToBook = (customer) => Boolean(
    customer.firstName?.trim()
    && customer.phone?.replace(/\D/g, '').length >= 10
    && EMAIL_PATTERN.test(customer.email?.trim() || '')
    && (customer.document || '').replace(/\D/g, '').length === 11
);

const StepCustomerInfo = ({ companyUuid, customerData, onSubmit, onBack }) => {
    const { t } = useTranslation();

    const splitCustomerName = (customer) => {
        const fullName = customer?.fullName || `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
        const parts = fullName.split(/\s+/).filter(Boolean);

        return {
            firstName: customer?.firstName || parts[0] || '',
            lastName: customer?.lastName || parts.slice(1).join(' ')
        };
    };

    // Phase: 'cpf' | 'form'
    const [phase, setPhase] = useState(customerData?.document ? 'form' : 'cpf');
    const [cpf, setCpf] = useState(customerData?.document ? formatCpf(customerData.document) : '');
    // Quem entrou pelo e-mail sem CPF cadastrado precisa digitá-lo no formulário, então o
    // campo deixa de ser apenas a confirmação do que foi usado na identificação.
    const [documentEditable, setDocumentEditable] = useState(!customerData?.document);
    const [lookingUp, setLookingUp] = useState(false);
    const [lookupError, setLookupError] = useState('');
    const [quickRegistration, setQuickRegistration] = useState(false);

    const [formData, setFormData] = useState({
        firstName: customerData?.firstName || '',
        lastName: customerData?.lastName || '',
        phone: customerData?.phone || '',
        email: customerData?.email || '',
        document: customerData?.document || '',
        // Vem do lookup e sobe junto com os dados: é o que permite marcar o clube já na
        // escolha dos serviços, sem esperar o preview de preço da confirmação.
        ...lookupStatusFields(customerData || {})
    });
    const [errors, setErrors] = useState({});

    const handleCpfChange = (e) => {
        setCpf(formatIdentifier(e.target.value));
        setLookupError('');
    };

    const handleCpfSubmit = async (e) => {
        e.preventDefault();

        const byEmail = isEmailIdentifier(cpf);
        if (!isCompleteIdentifier(cpf)) {
            setLookupError(t(byEmail ? 'validation.invalid.email' : 'validation.invalid.cpf'));
            return;
        }

        // O backend aceita os dois no mesmo parâmetro e decide pela presença do "@".
        const identifier = toIdentifierParam(cpf);
        const digits = cpf.replace(/\D/g, '');

        try {
            setLookingUp(true);
            setLookupError('');
            const response = await lookupCustomerByDocument(companyUuid, identifier);
            const customer = response.data;
            const { firstName, lastName } = splitCustomerName(customer);
            const customerDocument = byEmail
                ? (customer.document || '').replace(/\D/g, '')
                : digits;
            const identifiedCustomer = {
                firstName,
                lastName,
                phone: customer.phone || '',
                email: byEmail ? (customer.email || cpf.trim()) : (customer.email || ''),
                document: customerDocument,
                ...lookupStatusFields(customer)
            };
            setFormData(identifiedCustomer);
            // Só fica travado quando o próprio CPF foi a chave da busca; o cadastro
            // encontrado pelo e-mail sem documento precisa do campo aberto.
            setDocumentEditable(customerDocument.length !== 11);

            // Cadastro completo não tem o que conferir: quem já é cliente segue direto para o
            // agendamento. Falta de e-mail, de telefone ou de CPF (todos obrigatórios) ainda
            // cai no formulário, que é onde esses dados são completados.
            if (isReadyToBook(identifiedCustomer)) {
                onSubmit(identifiedCustomer);
                return;
            }

            setPhase('form');
        } catch (err) {
            if (err.response?.status === 404) {
                // Not found: open blank form for registration
                setFormData(prev => ({
                    ...prev,
                    document: byEmail ? '' : digits,
                    firstName: '',
                    lastName: '',
                    phone: '',
                    email: byEmail ? cpf.trim() : '',
                    ...lookupStatusFields()
                }));
                setDocumentEditable(byEmail);
                setPhase('form');
            } else {
                setLookupError(t('booking.lookup_error'));
            }
        } finally {
            setLookingUp(false);
        }
    };

    /** O estado guarda o CPF só com dígitos; a máscara existe apenas na exibição. */
    const handleDocumentChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
        setFormData(prev => ({ ...prev, document: digits }));
        if (errors.document) {
            setErrors(prev => ({ ...prev, document: '' }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        // CPF é obrigatório para agendar, inclusive para quem se identificou pelo e-mail:
        // o cadastro importado sem documento é completado aqui, uma vez só.
        const documentDigits = (formData.document || '').replace(/\D/g, '');
        if (!documentDigits) {
            newErrors.document = t('validation.required.cpf');
        } else if (documentDigits.length !== 11) {
            newErrors.document = t('validation.invalid.cpf');
        }

        if (!formData.firstName.trim()) {
            newErrors.firstName = t('validation.required.firstName');
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = t('validation.min.generic', { field: t('firstName'), qty: 2 });
        }

        if (formData.lastName.trim() && formData.lastName.trim().length < 2) {
            newErrors.lastName = t('validation.min.generic', { field: t('lastName'), qty: 2 });
        }

        if (!formData.phone.trim()) {
            newErrors.phone = t('validation.required.phone');
        } else if (formData.phone.replace(/\D/g, '').length < 10) {
            newErrors.phone = t('validation.invalid.phone');
        }

        // Endereço errado vira confirmação e lembrete que nunca chegam, então o formato é
        // validado junto com a obrigatoriedade.
        if (!formData.email.trim()) {
            newErrors.email = t('validation.required.email');
        } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
            newErrors.email = t('validation.invalid.email');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        if (!quickRegistration) {
            onSubmit({ ...formData, document: formData.document });
            return;
        }

        // O cadastro direto continua identificando CPF/e-mail antes de avançar: assim quem já
        // é cliente mantém o histórico e os benefícios do clube, sem criar um registro paralelo.
        try {
            setLookingUp(true);
            setLookupError('');
            const lookup = async (identifier) => {
                try {
                    return (await lookupCustomerByDocument(companyUuid, identifier)).data;
                } catch (error) {
                    if (error.response?.status === 404) return null;
                    throw error;
                }
            };

            let customer = await lookup(formData.document.replace(/\D/g, ''));
            let matchedByEmail = false;
            if (!customer) {
                customer = await lookup(formData.email.trim());
                matchedByEmail = Boolean(customer);
            }

            if (!customer) {
                onSubmit({ ...formData, document: formData.document });
                return;
            }

            const storedDocument = (customer.document || '').replace(/\D/g, '');
            const enteredDocument = formData.document.replace(/\D/g, '');
            if (matchedByEmail && storedDocument && storedDocument !== enteredDocument) {
                setLookupError(t('booking.registration_identity_mismatch'));
                return;
            }

            const { firstName, lastName } = splitCustomerName(customer);
            onSubmit({
                ...formData,
                firstName: customer.firstName || firstName || formData.firstName,
                lastName: customer.lastName || lastName || formData.lastName,
                phone: customer.phone || formData.phone,
                email: customer.email || formData.email.trim(),
                document: storedDocument || enteredDocument,
                ...lookupStatusFields(customer)
            });
        } catch (error) {
            setLookupError(t('booking.lookup_error'));
        } finally {
            setLookingUp(false);
        }
    };

    const startRegistration = () => {
        const identifier = cpf.trim();
        const byEmail = isEmailIdentifier(identifier);
        setFormData(prev => ({
            ...prev,
            firstName: '',
            lastName: '',
            phone: '',
            email: byEmail ? identifier : '',
            document: !byEmail && looksLikeDocument(identifier) ? identifier.replace(/\D/g, '') : '',
            ...lookupStatusFields()
        }));
        setDocumentEditable(true);
        setQuickRegistration(true);
        setLookupError('');
        setErrors({});
        setPhase('form');
    };

    // ── Phase: enter CPF ──────────────────────────────────────────────────
    if (phase === 'cpf') {
        return (
            <div className="step-customer booking-identify">
                <div className="booking-identify__badge">
                    <i className="fa fa-id-card"></i>
                </div>
                <h4 className="booking-identify__title">{t('booking.enter_cpf_title')}</h4>
                <p className="booking-identify__subtitle">{t('booking.enter_cpf_description')}</p>

                <div className="booking-identify__trust" role="note">
                    <i className="fa fa-lock" aria-hidden="true"></i>
                    <span>{t('booking.identify_privacy')}</span>
                </div>

                <Form onSubmit={handleCpfSubmit} className="booking-identify__form">
                    {/* Campo único da tela: o rótulo vira microtexto acima do valor, dentro da
                        mesma moldura, para não competir com o número que o cliente digita. */}
                    <div className={`booking-identify__field${lookupError ? ' has-error' : ''}`}>
                        <label htmlFor="booking-cpf">{t('booking.identify_label')}</label>
                        <input
                            id="booking-cpf"
                            type="text"
                            value={cpf}
                            onChange={handleCpfChange}
                            placeholder={t('booking.identify_placeholder')}
                            // Vazio começa com teclado de e-mail para permitir as duas opções;
                            // após o primeiro dígito, o CPF pode usar o teclado numérico.
                            inputMode={cpf && looksLikeDocument(cpf) ? 'numeric' : 'email'}
                            maxLength={looksLikeDocument(cpf) ? 14 : 100}
                            autoComplete="off"
                            autoFocus
                        />
                    </div>

                    {lookupError && (
                        <Alert variant="danger" className="booking-identify__error py-2">
                            {lookupError}
                        </Alert>
                    )}

                    <Button
                        variant="primary"
                        type="submit"
                        disabled={lookingUp}
                        className="booking-identify__submit"
                    >
                        {lookingUp
                            ? <><Spinner size="sm" className="me-2" />{t('booking.searching')}</>
                            : <>{t('continue')}<i className="fa fa-arrow-right ms-2"></i></>
                        }
                    </Button>

                    {onBack && (
                        <Button variant="link" type="button" onClick={onBack} className="booking-identify__back">
                            <i className="fa fa-arrow-left me-2"></i>
                            {t('back')}
                        </Button>
                    )}
                </Form>

                <Button
                    variant="link"
                    type="button"
                    className="booking-identify__register"
                    onClick={startRegistration}
                >
                    {t('booking.create_customer_cta')}
                </Button>

                {/* A barra com "Meus agendamentos" só existe depois da identificação, e esta é
                    a primeira tela do site: quem entrou para consultar ou cancelar não via
                    saída nenhuma e achava que precisava marcar de novo. */}
                <a
                    className="booking-identify__my-appointments"
                    href={`/booking/${companyUuid}/my-appointments`}
                >
                    <i className="fa fa-calendar-check me-2" aria-hidden="true"></i>
                    {t('booking.already_booked_cta')}
                </a>
            </div>
        );
    }

    // ── Phase: fill / confirm details ─────────────────────────────────────
    return (
        <div className="step-customer">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-user me-2 text-primary"></i>
                    {t('booking.your_info')}
                </h4>
                <p className="step-subtitle text-muted">
                    {t('booking.your_info_description')}
                </p>
            </div>

            {/* Quem cai neste formulário tem cadastro incompleto e ainda não passou pela
                página, que é onde o aviso de atraso mora. Repetir aqui evita ele completar
                o cadastro e só então descobrir que não vai conseguir agendar. */}
            {formData.clubPaymentOverdue && (
                <Alert variant="warning" className="mb-4">
                    <i className="fa fa-triangle-exclamation me-2"></i>
                    <strong>Você tem mensalidade do clube em aberto.</strong> Enquanto ela estiver pendente,
                    o agendamento pelo site fica indisponível. Entre em contato para regularizar.
                </Alert>
            )}

            {/* Mesmo motivo do aviso acima: quem completa o cadastro aqui ainda não viu a página,
                e a falta ou o cancelamento em cima da hora vão barrar a conclusão. */}
            {hasActiveOnlineBookingBlock(formData) && (
                <Alert variant="warning" className="mb-4">
                    <i className="fa fa-triangle-exclamation me-2"></i>
                    <strong>Agendamento pelo site indisponível no momento.</strong>{' '}
                    {formData.onlineBookingBlockMessage}
                </Alert>
            )}

            {formData.subscriptionCustomer && (
                <div className="booking-club-banner mb-4">
                    <i className="fa fa-crown"></i>
                    <div>
                        <strong>Você é assinante{formData.planName ? ` do ${formData.planName}` : ''}.</strong>
                        <span>
                            {formData.planBenefitDays?.length > 0
                                ? `Seu plano dá benefício em: ${formData.planBenefitDays.join(', ')}.`
                                : 'Os serviços inclusos aparecem marcados no próximo passo.'}
                        </span>
                    </div>
                </div>
            )}

            {lookupError && (
                <Alert variant="danger" className="booking-identify__error py-2">
                    {lookupError}
                </Alert>
            )}

            <Form onSubmit={handleFormSubmit}>
                <Row className="g-3 mb-1">
                    <Col md={12}>
                        <Form.Group>
                            {/* O asterisco aparece mesmo com o campo travado: o CPF é exigido
                                para agendar, e o cliente identificado pelo documento precisa
                                enxergar a mesma regra de quem vai digitá-lo. */}
                            <Form.Label>
                                CPF <span className="text-danger">*</span>
                            </Form.Label>
                            {/* Identificado pelo CPF, ele só é exibido para conferência: para trocar,
                                o cliente volta e refaz a busca. Identificado pelo e-mail sem documento
                                no cadastro, o campo abre porque o CPF é obrigatório para agendar. */}
                            <Form.Control
                                type="text"
                                name="document"
                                value={formatCpf(formData.document || '')}
                                onChange={documentEditable ? handleDocumentChange : undefined}
                                readOnly={!documentEditable}
                                placeholder="000.000.000-00"
                                inputMode="numeric"
                                maxLength={14}
                                isInvalid={!!errors.document}
                                className="form-control-lg"
                                aria-label="CPF"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.document}
                            </Form.Control.Feedback>
                            {documentEditable && (
                                <Form.Text className="text-muted">
                                    {t('booking.document_required_hint')}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>{t('firstName')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder={t('firstName')}
                                isInvalid={!!errors.firstName}
                                className="form-control-lg"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.firstName}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>{t('lastName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder={t('lastName')}
                                isInvalid={!!errors.lastName}
                                className="form-control-lg"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.lastName}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>{t('phone')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="(00) 00000-0000"
                                isInvalid={!!errors.phone}
                                className="form-control-lg"
                            />
                            {errors.phone && (
                                <div className="invalid-feedback d-block">
                                    {errors.phone}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>{t('email')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="nome@email.com"
                                isInvalid={!!errors.email}
                                className="form-control-lg"
                                inputMode="email"
                                autoComplete="email"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.email}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                {t('booking.email_hint')}
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
                <div className="step-actions d-flex justify-content-between mt-4">
                    <Button variant="outline-secondary" type="button" onClick={() => {
                        setQuickRegistration(false);
                        setPhase('cpf');
                    }}>
                        <i className="fa fa-arrow-left me-2"></i>
                        {t('back')}
                    </Button>
                    <Button variant="primary" type="submit" disabled={lookingUp || formData.clubPaymentOverdue}>
                        {lookingUp
                            ? <><Spinner size="sm" className="me-2" />{t('booking.searching')}</>
                            : <>{t('booking.review_appointment')}<i className="fa fa-arrow-right ms-2"></i></>
                        }
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default StepCustomerInfo;
